// Pure filtering and option-building for the transactions view. Kept free of
// Svelte/DOM so the tab/drill-down/search rules and the category-suggestion
// ordering can be unit-tested directly.

import { UNCAT, isUncategorised, categoryOrUncat, INCOME_TYPES } from './category';
import { isSplitOutOfDate } from './split';
import type { Transaction, Category } from './db';

export type TxFilter = 'all' | 'uncategorised' | 'excluded' | 'transfers';

/** A transaction plus its precomputed lower-cased search haystack. */
export type TransactionView = Transaction & { searchText: string };

/** Manual pay-cycle shift, normalised to -1/0/1. */
export type Offset = -1 | 0 | 1;

export function txOffset(tx: Transaction): Offset {
	const v = tx.monthOffset;
	if (v === -1 || v === 1) return v;
	return 0;
}

/** The pay-cycle month a transaction is currently keyed under (from its pk). */
export function derivedMonthOf(tx: Transaction): string {
	return tx.pk.replace(/^MONTH#/, '');
}

/** "Mar 2026"-style label for a YYYY-MM month key. */
export function shortMonthLabel(yyyymm: string): string {
	const [y, m] = yyyymm.split('-').map(Number);
	if (!y || !m) return yyyymm;
	return new Date(y, m - 1).toLocaleDateString('en-NZ', { month: 'short', year: 'numeric' });
}

/** True when a transaction carries split lines. */
export function isSplit(tx: Transaction): boolean {
	return !!(tx.splits && tx.splits.length > 0);
}

/** True when a split exists but no longer sums to the transaction amount. */
export function splitOutOfDate(tx: Transaction): boolean {
	return isSplit(tx) && isSplitOutOfDate(tx.amount, tx.splits);
}

/** Lower-cased haystack of the fields a transaction search should match. */
export function buildSearchText(tx: Transaction): string {
	return [tx.description, tx.merchant?.name, tx.meta?.particulars, tx.meta?.code, tx.meta?.reference]
		.filter(Boolean)
		.join(' ')
		.toLowerCase();
}

export interface FilterParams {
	/** Active tab. Ignored when `categoryFilter` is set (drill-down wins). */
	filter: TxFilter;
	/** Budget category drill-down (from a URL param), or null. */
	categoryFilter: string | null;
	/** Lower-cased search query, or empty. */
	searchQuery: string;
}

type Searchable = Transaction & { searchText: string };

/**
 * Apply the active tab (or category drill-down) then the search query.
 *
 * - category drill-down: the category itself or any split line in it;
 * - uncategorised: a clean to-do list (no transfers, no excluded rows);
 * - excluded / transfers: just those rows;
 * - all: everything except own-account transfers.
 */
export function filterTransactions<T extends Searchable>(
	transactions: T[],
	{ filter, categoryFilter, searchQuery }: FilterParams
): T[] {
	let result = transactions;

	if (categoryFilter) {
		result = result.filter(
			(t) =>
				categoryOrUncat(t.category) === categoryFilter ||
				(t.splits?.some((s) => s.category === categoryFilter) ?? false)
		);
	} else if (filter === 'uncategorised') {
		result = result.filter(
			(t) => isUncategorised(t.category) && !t.excludeFromBudget && !t.isTransfer
		);
	} else if (filter === 'excluded') {
		result = result.filter((t) => t.excludeFromBudget);
	} else if (filter === 'transfers') {
		result = result.filter((t) => t.isTransfer);
	} else {
		result = result.filter((t) => !t.isTransfer);
	}

	if (searchQuery) {
		result = result.filter((t) => t.searchText.includes(searchQuery));
	}

	return result;
}

export interface CategoryOption {
	label: string;
	value: string;
	/** True when the type matches the transaction's income/expense polarity. */
	suggested: boolean;
}

/**
 * Category choices for a transaction, ordered suggested-first then alphabetical
 * and filtered by `query`. Income transactions suggest income-typed categories;
 * expenses suggest everything else (including untyped). Uncategorised is always
 * offered and always suggested.
 */
export function getCategoryOptions(
	cats: Category[],
	query: string,
	txAmount: number
): CategoryOption[] {
	const q = query.trim().toLowerCase();
	const isIncome = txAmount > 0;

	const options: CategoryOption[] = cats.map((cat) => {
		const isIncomeType = INCOME_TYPES.has(cat.type || '');
		return { label: cat.name, value: cat.name, suggested: isIncome ? isIncomeType : !isIncomeType };
	});

	options.unshift({ label: 'Uncategorised', value: UNCAT, suggested: true });

	options.sort((a, b) => {
		if (a.suggested !== b.suggested) return a.suggested ? -1 : 1;
		return a.label.localeCompare(b.label);
	});

	return q ? options.filter((opt) => opt.label.toLowerCase().includes(q)) : options;
}
